import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, Link2, Loader2 } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useLogout } from "@/hooks/useLogout";
import { useUserAvatar } from "@/hooks/useUserAvatar";
import { useSidebar } from "@/contexts/SidebarContext";
import { cn } from "@/components/utils/utils";
import { useI18n } from '@/hooks/useI18n';
import { NotificationBell } from "./NotificationBell";
import { UserAvatarMenu } from "./UserAvatarMenu";
import { ReactNode, useState } from "react";
import { useToast } from "@/hooks/useToast";

interface HeaderProps {
  pageTitle?: string;
  pageDescription?: string;
  showBackButton?: boolean;
  showCtaButton?: boolean;
  headerAction?: ReactNode;
}

const Header = ({
  pageTitle = "Dashboard",
  pageDescription = "Welcome to your dashboard",
  showBackButton = true,
  showCtaButton = false,
  headerAction,
}: HeaderProps) => {
  const { user, userRole } = useAuth();
  const { logout, isLoggingOut } = useLogout();
  const { displayName } = useUserAvatar();
  const navigate = useNavigate();
  const { toggleMobileSidebar, isSidebarCollapsed } = useSidebar();
  const { t } = useI18n();
  const { toast } = useToast();
  const [isCopyingLink, setIsCopyingLink] = useState(false);

  const handleLogout = () => {
    logout("/login");
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleCopyTripRequestLink = async () => {
    if (!user) return;
    setIsCopyingLink(true);
    await new Promise((resolve) => setTimeout(resolve, 600));
    const path =
      userRole === "travel_designer" || userRole === "travel_advisor_member" || userRole === "agency"
        ? `/#/request-trip?td=${user.id}`
        : "/#/request-trip";
    const url = `${window.location.origin}${path}`;
    await navigator.clipboard.writeText(url);
    setIsCopyingLink(false);
    toast({ title: t("common.linkCopied") });
  };

  return (
    <header
      className={cn(
        "fixed top-0 z-50 bg-white border-b border-gray-100 transition-all duration-300 ease-in-out",
        // Mobile: full width, Desktop: positioned relative to sidebar
        "left-0 right-0 md:left-auto md:right-0",
        // Desktop positioning based on sidebar state
        isSidebarCollapsed ? "md:left-[70px]" : "md:left-[222px]",
      )}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20 md:h-[80px]">
          {/* Mobile Title */}
          <div className="md:hidden">
            <div>
              <h3 className="text-gray-900">
                {pageTitle}
              </h3>
              <p className="text-gray-600 text-sm">{pageDescription}</p>
            </div>
          </div>

          {/* Desktop Page Title and Description */}
          <div className="hidden md:block">
            <h3 className="text-gray-900">
              {/* {showBackButton && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleBack}
                  className="h-8 w-8 p-0 hover:bg-gray-100 mr-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
              )} */}
              {pageTitle}
            </h3>
            <p className="text-sm text-gray-600">{pageDescription}</p>
          </div>

          {/* User Menu Section */}
          <div className="flex items-center gap-2 md:gap-4">
            {user && (
              <>
                {/* Custom Header Action or Default New Trip Button - Only on Desktop */}
                <div className="relative hidden md:block">
                  {headerAction || (showCtaButton && (
                    <Button
                      variant="default"
                      size="lg"
                      className="h-8 px-3"
                      disabled={isCopyingLink}
                      onClick={handleCopyTripRequestLink}
                    >
                      {isCopyingLink
                        ? <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        : <Link2 className="h-4 w-4 mr-1" />}
                      {t("navigation.shareTripRequestLink")}
                    </Button>
                  ))}
                </div>

                <NotificationBell />

                {/* User Avatar & Dropdown */}
                <UserAvatarMenu
                  displayName={displayName}
                  email={user.email ?? ''}
                  onLogout={handleLogout}
                  isLoggingOut={isLoggingOut}
                />

                {/* Mobile Menu Button - Now on the right */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={toggleMobileSidebar}
                  className="h-10 w-10 md:h-8 md:w-8 p-0 hover:bg-gray-100 md:hidden"
                >
                  <Menu className="h-5 w-5 md:h-4 md:w-4" />
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
